import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Button } from "@babylonjs/gui/2D/controls/button";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { getLang } from "./i18n";

/**
 * SERVER CAPACITY & QUEUE
 * ------------------------------------------------------------------
 * Prevents saturating the per-game microservices. When a game's rooms are full,
 * new players are placed in a fair FIFO queue with a live position + ETA and a
 * friendly message — never a hard error.
 *
 * REST contract (prod backend — the client below is transport-agnostic):
 *   GET  /kq/capacity/:game            -> { capacity, load, queued }
 *   POST /kq/queue/join {game}         -> { admitted, ticket?, position?, etaS?, capacity, load }
 *   GET  /kq/queue/:ticket             -> { admitted, position?, etaS?, capacity, load }
 *   POST /kq/queue/:ticket/cancel      -> 204
 *   POST /kq/rooms/:game/release       -> 204   (on scene exit / disconnect)
 * The real `capacity` is derived from microservice health (Colyseus rooms, pod
 * CPU, HPA replicas). Here we ship a faithful in-memory simulator so it runs
 * standalone; swap `LocalCapacitySim` for a `fetch`-based transport in prod.
 */

export interface SeatResult {
  admitted: boolean;
  ticket?: string;
  position?: number;
  etaS?: number;
  capacity: number;
  load: number;
  queued: number;
}

export interface CapacityTransport {
  requestSeat(game: string): Promise<SeatResult>;
  poll(ticket: string): Promise<SeatResult>;
  cancel(ticket: string): void;
  release(game: string): void;
  status(game: string): { capacity: number; load: number; queued: number };
}

interface GameState {
  capacity: number;
  load: number;
  queue: string[]; // ticket ids, FIFO
}

const AVG_SERVICE_S = 12; // avg time a slot is held → ETA basis

/** In-memory capacity simulator with realistic churn so queues actually drain. */
export class LocalCapacitySim implements CapacityTransport {
  private games = new Map<string, GameState>();
  private ticketGame = new Map<string, string>();
  private seq = 0;

  /** @param defaultCapacity small (e.g. 3) so the queue is visible in the demo. */
  constructor(private readonly defaultCapacity = 3) {
    // Players elsewhere finish over time → free slots → drain queues.
    setInterval(() => this.churn(), 3500);
  }

  private state(game: string): GameState {
    let s = this.games.get(game);
    if (!s) {
      s = { capacity: this.defaultCapacity, load: 0, queue: [] };
      this.games.set(game, s);
    }
    return s;
  }

  /** Seed realistic initial occupancy so the hub shows varied load (some full). */
  seed(slugs: string[]): void {
    for (const g of slugs) {
      const s = this.state(g);
      s.load = Math.floor(Math.random() * (s.capacity + 1)); // 0..capacity
    }
  }

  private churn(): void {
    for (const s of this.games.values()) {
      if (s.load > 0 && Math.random() < 0.6) {
        s.load--;
        this.admitNext(s);
      }
    }
  }

  private admitNext(s: GameState): void {
    while (s.load < s.capacity && s.queue.length > 0) {
      s.queue.shift();
      s.load++;
    }
  }

  async requestSeat(game: string): Promise<SeatResult> {
    const s = this.state(game);
    if (s.load < s.capacity) {
      s.load++;
      return { admitted: true, capacity: s.capacity, load: s.load, queued: s.queue.length };
    }
    const ticket = `t${++this.seq}`;
    s.queue.push(ticket);
    this.ticketGame.set(ticket, game);
    const position = s.queue.indexOf(ticket) + 1;
    return { admitted: false, ticket, position, etaS: position * AVG_SERVICE_S, capacity: s.capacity, load: s.load, queued: s.queue.length };
  }

  async poll(ticket: string): Promise<SeatResult> {
    const game = this.ticketGame.get(ticket);
    if (!game) return { admitted: true, capacity: 0, load: 0, queued: 0 }; // unknown → let through
    const s = this.state(game);
    const idx = s.queue.indexOf(ticket);
    if (idx === -1) {
      // no longer queued → admitted
      this.ticketGame.delete(ticket);
      return { admitted: true, capacity: s.capacity, load: s.load, queued: s.queue.length };
    }
    const position = idx + 1;
    return { admitted: false, position, etaS: position * AVG_SERVICE_S, capacity: s.capacity, load: s.load, queued: s.queue.length };
  }

  cancel(ticket: string): void {
    const game = this.ticketGame.get(ticket);
    if (!game) return;
    const s = this.state(game);
    const idx = s.queue.indexOf(ticket);
    if (idx !== -1) s.queue.splice(idx, 1);
    this.ticketGame.delete(ticket);
  }

  release(game: string): void {
    const s = this.state(game);
    if (s.load > 0) s.load--;
    this.admitNext(s);
  }

  status(game: string): { capacity: number; load: number; queued: number } {
    const s = this.state(game);
    return { capacity: s.capacity, load: s.load, queued: s.queue.length };
  }
}

/**
 * REST transport — wires the Babylon hub onto the REAL server-authoritative
 * capacity API merged in developmain (PR 298):
 *   POST   {base}/api/v1/family-fun/capacity/admission   → 200 admitted | queued ticket
 *   GET    {base}/api/v1/family-fun/capacity/queue/{id}  → TicketView (position, est_wait_s)
 *   DELETE {base}/api/v1/family-fun/capacity/queue/{id}  → leave queue
 *   POST   {base}/api/v1/family-fun/capacity/queue/{id}/claim → claim admitted seat
 *   POST   {base}/api/v1/family-fun/capacity/release     → free slot on exit
 *   GET    {base}/api/v1/family-fun/capacity/status      → coarse band + queue depth
 *
 * FAIL-OPEN: only an explicit queued body means "queued" — any network/4xx/5xx
 * (BE absent on the standalone preview) resolves to admitted so game launches
 * never regress behind this feature. The server-side WS gate is the backstop.
 */
export class RestCapacityTransport implements CapacityTransport {
  private sessionCodes = new Map<string, string>(); // game → session_code
  private ticketGame = new Map<string, string>();
  private statusCache: { band: "ok" | "busy" | "queue"; queued: number } = { band: "ok", queued: 0 };

  constructor(private readonly base: string, private readonly authToken?: string) {
    void this.refreshStatus();
    setInterval(() => void this.refreshStatus(), 15000);
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = { "Content-Type": "application/json" };
    if (this.authToken) h.Authorization = `Bearer ${this.authToken}`;
    return h;
  }

  private async refreshStatus(): Promise<void> {
    try {
      const r = await fetch(`${this.base}/api/v1/family-fun/capacity/status`, { headers: this.headers() });
      if (!r.ok) return;
      const b = await r.json();
      const d = b?.data ?? b;
      this.statusCache = { band: d?.status ?? "ok", queued: Number(d?.queue_depth ?? 0) };
    } catch {
      /* keep last snapshot — fail-open */
    }
  }

  async requestSeat(game: string): Promise<SeatResult> {
    const sessionCode = `kq-${game}-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
    try {
      const r = await fetch(`${this.base}/api/v1/family-fun/capacity/admission`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ game_type: game, mode: "solo", session_code: sessionCode }),
      });
      const b = await r.json().catch(() => null);
      const d = b?.data ?? b;
      if (d && d.admitted === false && d.ticket) {
        const tk = d.ticket;
        this.ticketGame.set(tk.ticket_id, game);
        return {
          admitted: false,
          ticket: tk.ticket_id,
          position: tk.position ?? 1,
          etaS: Math.round(tk.est_wait_s ?? 0),
          capacity: 1,
          load: 1,
          queued: tk.queue_depth ?? 0,
        };
      }
      this.sessionCodes.set(game, d?.session_code ?? sessionCode);
      return { admitted: true, capacity: 1, load: 0, queued: this.statusCache.queued };
    } catch {
      // fail-open — never block a launch on a failed fetch
      this.sessionCodes.set(game, sessionCode);
      return { admitted: true, capacity: 1, load: 0, queued: 0 };
    }
  }

  async poll(ticket: string): Promise<SeatResult> {
    try {
      const r = await fetch(`${this.base}/api/v1/family-fun/capacity/queue/${encodeURIComponent(ticket)}`, { headers: this.headers() });
      if (r.status === 404) return { admitted: true, capacity: 1, load: 0, queued: 0 }; // expired → fail-open
      const b = await r.json().catch(() => null);
      const d = b?.data ?? b;
      if (d?.status === "admitted" || d?.status === "claimed") {
        // claim the seat, then enter
        const game = this.ticketGame.get(ticket);
        try {
          const c = await fetch(`${this.base}/api/v1/family-fun/capacity/queue/${encodeURIComponent(ticket)}/claim`, { method: "POST", headers: this.headers() });
          const cb = await c.json().catch(() => null);
          const cd = cb?.data ?? cb;
          if (game && cd?.session_code) this.sessionCodes.set(game, cd.session_code);
        } catch {
          /* claim best-effort */
        }
        this.ticketGame.delete(ticket);
        return { admitted: true, capacity: 1, load: 0, queued: d?.queue_depth ?? 0 };
      }
      return {
        admitted: false,
        position: d?.position ?? 1,
        etaS: Math.round(d?.est_wait_s ?? 0),
        capacity: 1,
        load: 1,
        queued: d?.queue_depth ?? 0,
      };
    } catch {
      // transient — keep the caller polling with last-known view
      return { admitted: false, position: 1, etaS: AVG_SERVICE_S, capacity: 1, load: 1, queued: 0 };
    }
  }

  cancel(ticket: string): void {
    void fetch(`${this.base}/api/v1/family-fun/capacity/queue/${encodeURIComponent(ticket)}`, { method: "DELETE", headers: this.headers() }).catch(() => undefined);
    this.ticketGame.delete(ticket);
  }

  release(game: string): void {
    const code = this.sessionCodes.get(game);
    if (!code) return;
    void fetch(`${this.base}/api/v1/family-fun/capacity/release`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ session_code: code }),
    }).catch(() => undefined);
    this.sessionCodes.delete(game);
  }

  status(_game: string): { capacity: number; load: number; queued: number } {
    // Coarse band → chip mapping: "queue" renders as full, "busy" as loaded.
    const { band, queued } = this.statusCache;
    if (band === "queue") return { capacity: 1, load: 1, queued };
    if (band === "busy") return { capacity: 2, load: 1, queued };
    return { capacity: 1, load: 0, queued: 0 };
  }
}

/** Handle returned to the caller so it can release its seat on exit. */
export interface SeatHandle {
  release: () => void;
}

/** Thrown when the user cancels the queue wait. */
export class QueueCancelled extends Error {
  constructor() {
    super("queue_cancelled");
  }
}

const OCEAN = "#0e2233";
const GOLD = "#d8b24a";

/** Orchestrates capacity checks + the friendly queue overlay. */
export class CapacityManager {
  constructor(private readonly transport: CapacityTransport) {}

  status(game: string) {
    return this.transport.status(game);
  }

  /**
   * Acquire a seat for `game`. Resolves once admitted (immediately, or after the
   * queue drains). Rejects with QueueCancelled if the user backs out.
   */
  async enter(game: string, gameTitle: string, adt: AdvancedDynamicTexture): Promise<SeatHandle> {
    const first = await this.transport.requestSeat(game);
    if (first.admitted) return { release: () => this.transport.release(game) };

    const ticket = first.ticket!;
    const overlay = new QueueOverlay(adt, gameTitle);
    overlay.update(first);

    return new Promise<SeatHandle>((resolve, reject) => {
      const timer = setInterval(async () => {
        const res = await this.transport.poll(ticket);
        overlay.update(res);
        if (res.admitted) {
          clearInterval(timer);
          overlay.dispose();
          resolve({ release: () => this.transport.release(game) });
        }
      }, 1500);

      overlay.onCancel = () => {
        clearInterval(timer);
        this.transport.cancel(ticket);
        overlay.dispose();
        reject(new QueueCancelled());
      };
    });
  }
}

/** Friendly, reassuring queue UI with live position + ETA + capacity. */
class QueueOverlay {
  private scrim: Rectangle;
  private posText: TextBlock;
  private etaText: TextBlock;
  private capText: TextBlock;
  private bar: Rectangle;
  private barFill: Rectangle;
  private maxSeen = 1;
  onCancel?: () => void;

  constructor(adt: AdvancedDynamicTexture, gameTitle: string) {
    const fr = getLang() === "fr";
    this.scrim = new Rectangle("qScrim");
    this.scrim.background = "rgba(3,8,14,0.82)";
    this.scrim.thickness = 0;
    this.scrim.zIndex = 70;
    adt.addControl(this.scrim);

    const card = new Rectangle("qCard");
    card.width = "540px";
    card.adaptHeightToChildren = true;
    card.cornerRadius = 18;
    card.thickness = 2;
    card.color = GOLD;
    card.background = OCEAN;
    this.scrim.addControl(card);

    const stack = new StackPanel();
    stack.paddingLeft = "26px";
    stack.paddingRight = "26px";
    stack.paddingTop = "24px";
    stack.paddingBottom = "24px";
    card.addControl(stack);

    stack.addControl(txt("⏳", 44, GOLD, "56px"));
    stack.addControl(txt(fr ? "Salle pleine — un instant" : "Room full — one moment", 23, GOLD, "36px"));
    stack.addControl(
      txt(
        fr
          ? `Beaucoup de pèlerins jouent à « ${gameTitle} » en ce moment. 🙏\nTu entreras dès qu'une place se libère.`
          : `Many pilgrims are playing "${gameTitle}" right now. 🙏\nYou'll enter as soon as a seat frees up.`,
        15,
        "#dfe7ee",
        "56px",
      ),
    );

    this.posText = txt("", 20, "#f2ede0", "32px");
    stack.addControl(this.posText);
    this.etaText = txt("", 15, "#a9d6c0", "26px");
    stack.addControl(this.etaText);

    this.bar = new Rectangle("qbar");
    this.bar.height = "14px";
    this.bar.width = "440px";
    this.bar.cornerRadius = 7;
    this.bar.color = GOLD;
    this.bar.thickness = 1;
    this.bar.background = "#12283a";
    this.bar.paddingTop = "10px";
    stack.addControl(this.bar);
    this.barFill = new Rectangle("qbarfill");
    this.barFill.height = 1;
    this.barFill.width = "10%";
    this.barFill.thickness = 0;
    this.barFill.background = GOLD;
    this.barFill.horizontalAlignment = 0; // left
    this.bar.addControl(this.barFill);

    this.capText = txt("", 13, "#7fa6bd", "24px");
    this.capText.paddingTop = "8px";
    stack.addControl(this.capText);

    const cancel = Button.CreateSimpleButton("qCancel", fr ? "Annuler et revenir" : "Cancel and go back");
    cancel.width = "200px";
    cancel.height = "40px";
    cancel.color = GOLD;
    cancel.background = "transparent";
    cancel.cornerRadius = 10;
    cancel.paddingTop = "14px";
    cancel.onPointerUpObservable.add(() => this.onCancel?.());
    stack.addControl(cancel);
  }

  update(res: SeatResult): void {
    const fr = getLang() === "fr";
    const pos = res.position ?? 1;
    this.maxSeen = Math.max(this.maxSeen, pos);
    const ord = fr ? `${pos}ᵉ` : ordinal(pos);
    this.posText.text = fr ? `Tu es ${ord} dans la file` : `You are ${ord} in line`;
    const eta = res.etaS ?? pos * AVG_SERVICE_S;
    this.etaText.text = fr ? `Temps d'attente estimé ~${eta} s` : `Estimated wait ~${eta} s`;
    const progress = Math.max(0.06, 1 - (pos - 1) / this.maxSeen);
    this.barFill.width = `${Math.round(progress * 100)}%`;
    this.capText.text = fr
      ? `Capacité du microservice : ${res.load}/${res.capacity} salles pleines · ${res.queued} en attente`
      : `Microservice capacity: ${res.load}/${res.capacity} rooms full · ${res.queued} waiting`;
  }

  dispose(): void {
    this.scrim.dispose();
  }
}

function txt(s: string, size: number, color: string, height: string): TextBlock {
  const tb = new TextBlock();
  tb.text = s;
  tb.color = color;
  tb.fontSize = size;
  tb.height = height;
  tb.textWrapping = true;
  return tb;
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
